import cn from 'classnames'
import s from '../ProductTile.module.scss'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { rememberPlpProductClick } from '@lib/hooks/cagegory/plp/usePLPScrollRestore'
import IconChevronLeft from '../../../icons/IconChevronLeft'
import IconChevronRight from '../../../icons/IconChevronRight'
import { useTranslation } from 'next-i18next/pages'

interface MobileImageSliderProps {
    productName: string
    productUrl: string
    image: any
    getSecondaryImage: any
    productId: string
    /** Conservado por compatibilidad; ya no se usa para la restauración. */
    categoryId?: string
}

const MobileImageSlider = ({
    productName,
    productUrl,
    image,
    getSecondaryImage,
    productId
}: MobileImageSliderProps): React.ReactElement => {
    const { t } = useTranslation('common')
    const router = useRouter()
    const containerRef = useRef<HTMLDivElement | null>(null)
    const touchStart = useRef({ x: 0, y: 0 })
    const didSwipe = useRef(false)
    const didScroll = useRef(false)
    const ignoreClick = useRef(false)
    const [activeIndex, setActiveIndex] = useState(0)
    const [dragOffset, setDragOffset] = useState(0)
    const [containerWidth, setContainerWidth] = useState(0)
    const [isDragging, setIsDragging] = useState(false)

    const slides = useMemo(
        () => [image, getSecondaryImage() || image],
        [image, getSecondaryImage]
    )

    const handleSliderClick = () => {
        rememberPlpProductClick(productId)
    }

    const navigateToPdp = () => {
        handleSliderClick()
        router.push(productUrl, undefined, { scroll: true }).then(() => {
            if (typeof window !== 'undefined') {
                window.scrollTo(0, 0)
            }
        })
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            navigateToPdp()
        }
    }

    const handleTouchStart = (e: React.TouchEvent) => {
        touchStart.current = {
            x: e.touches[0].clientX,
            y: e.touches[0].clientY
        }
        didSwipe.current = false
        didScroll.current = false
        setIsDragging(true)
    }

    const handleTouchMove = (e: React.TouchEvent) => {
        const dx = e.touches[0].clientX - touchStart.current.x
        const dy = e.touches[0].clientY - touchStart.current.y
        if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 4) {
            didScroll.current = true
            return
        }
        if (Math.abs(dx) < 4) {
            return
        }
        e.preventDefault()
        if (Math.abs(dx) > 8) {
            didSwipe.current = true
        }
        setDragOffset(dx)
    }

    const handleTouchEnd = () => {
        if (didSwipe.current) {
            ignoreClick.current = true
            window.setTimeout(() => {
                ignoreClick.current = false
            }, 400)
        }
        if (didScroll.current) {
            ignoreClick.current = true
            window.setTimeout(() => {
                ignoreClick.current = false
            }, 400)
        }
        if (containerWidth > 0) {
            const threshold = Math.max(40, containerWidth * 0.2)
            if (Math.abs(dragOffset) > threshold) {
                if (slides.length > 1) {
                    const nextIndex =
                        dragOffset < 0
                            ? (activeIndex + 1) % slides.length
                            : (activeIndex - 1 + slides.length) % slides.length
                    setActiveIndex(nextIndex)
                }
            }
        }
        setDragOffset(0)
        setIsDragging(false)
    }

    const handleClick = (e: React.MouseEvent) => {
        if (ignoreClick.current) {
            e.preventDefault()
            e.stopPropagation()
            return
        }
        e.preventDefault()
        navigateToPdp()
    }

    const handleTouchTap = (e: React.TouchEvent) => {
        if (ignoreClick.current || didSwipe.current || didScroll.current) {
            e.preventDefault()
            e.stopPropagation()
            return
        }
        e.preventDefault()
        navigateToPdp()
        ignoreClick.current = true
        window.setTimeout(() => {
            ignoreClick.current = false
        }, 400)
    }

    const handlePrev = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (slides.length <= 1) return
        setActiveIndex(
            (current) => (current - 1 + slides.length) % slides.length
        )
    }

    const handleNext = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (slides.length <= 1) return
        setActiveIndex((current) => (current + 1) % slides.length)
    }

    useEffect(() => {
        if (typeof window === 'undefined') return
        const measure = () => {
            const width =
                containerRef.current?.getBoundingClientRect().width || 0
            setContainerWidth(width)
        }
        measure()
        window.addEventListener('resize', measure)
        return () => window.removeEventListener('resize', measure)
    }, [])

    useEffect(() => {
        if (slides.length === 0) return
        if (activeIndex >= slides.length) {
            setActiveIndex(0)
        }
    }, [activeIndex, slides.length])

    const slidePercent = slides.length > 0 ? 100 / slides.length : 100
    const dragPercent =
        containerWidth > 0 ? (dragOffset / containerWidth) * slidePercent : 0
    const translatePercent = -activeIndex * slidePercent + dragPercent

    return (
        <div
            ref={containerRef}
            className="slider-plp-container"
            style={{ overflow: 'hidden', position: 'relative' }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchEnd}
        >
            <ul
                style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    width: `${slides.length * 100}%`,
                    transform: `translateX(${translatePercent}%)`,
                    transition: isDragging ? 'none' : 'transform 250ms ease'
                }}
            >
                {slides.map((slideImage, index) => (
                    <li
                        key={`${productId}-${index}`}
                        style={{
                            width: `${100 / slides.length}%`,
                            flex: `0 0 ${100 / slides.length}%`
                        }}
                    >
                        <button
                            type="button"
                            onClick={handleClick}
                            onKeyDown={handleKeyDown}
                            onTouchEnd={handleTouchTap}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                padding: 0,
                                width: '100%'
                            }}
                        >
                            <img
                                style={{ touchAction: 'pan-y' }}
                                src={slideImage}
                                alt={productName ? productName : 'placeholder'}
                                width={318}
                                height={318}
                                className={cn(s['product-img'], 'contain')}
                            />
                        </button>
                    </li>
                ))}
            </ul>
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 10px',
                    pointerEvents: 'none'
                }}
            >
                <button
                    type="button"
                    aria-label={t('slider.previous')}
                    onClick={handlePrev}
                    style={{
                        pointerEvents: 'auto',
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <IconChevronLeft />
                </button>
                <button
                    type="button"
                    aria-label={t('slider.next')}
                    onClick={handleNext}
                    style={{
                        pointerEvents: 'auto',
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <IconChevronRight />
                </button>
            </div>
        </div>
    )
}

export default MobileImageSlider
